import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../../../config';
import { debounce, getNewAuthToken, getOrgId } from '../../../../utils/common';

const createApiUrl = (apiData, search = '') => {
    const { module, path } = apiData;
    const modulePath = module.toLowerCase();
    let base_url = config.api.scogoApiUrl;
    if (apiData.platform === 'ICEBERG') {
        base_url = config.api.icebergApiUrl;
    }
    const url = `${base_url}/${config.api.version}/${modulePath}${path}?page=1&limit=100`;
    return search ? `${url}&q=${encodeURIComponent(search)}` : url;
};

// Responses are stored as { value, label } objects, but older ones can be raw values.
const getResponseValue = (response) => (response && typeof response === 'object' ? response.value : response);


export default function TaskInputBar({ task, index, handleSave, handleInputChange, handleMultiSelectChange, handleCloseInputBar }) {
    const [showOptions, setShowOptions] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [options, setOptions] = useState(task.options || []);
    const [allOptions, setAllOptions] = useState(task.options || []);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // Anchor kept in the normal tree so the chat panel this task belongs to can be found.
    const anchorRef = useRef(null);
    const [portalTarget, setPortalTarget] = useState(null);

    useLayoutEffect(() => {
        setPortalTarget(anchorRef.current?.closest('[data-chat-panel]') || document.body);
    }, []);

    const isDockedToChat = Boolean(portalTarget) && portalTarget !== document.body;


    const fetchOptions = useCallback(async (searchQuery = '') => {
        if ((task.input_type === 'SELECT' || task.input_type === 'MULTI_SELECT') &&
            task.cta && task.cta.length > 0) {

            const apiData = task.cta.find(e => e.call_on === "LOAD");
            if (apiData) {
                try {
                    setIsLoading(true);

                    const response = await axios.get(createApiUrl(apiData, searchQuery), {
                        headers: {
                            'x-org-id': getOrgId(),
                            'authorization': `Bearer ${getNewAuthToken()}`
                        }
                    });

                    const responseData = response?.data?.data;
                    if (responseData?.length > 0) {
                        const formattedOptions = responseData.map(item => ({
                            value: item.id,
                            label: item.name
                        }));
                        setAllOptions(formattedOptions);
                        setOptions(formattedOptions);
                    } else {
                        setAllOptions([]);
                        setOptions([]);
                    }
                } catch (error) {
                    console.error('Error fetching options:', error);
                    setAllOptions([]);
                    setOptions([]);
                } finally {
                    setIsLoading(false);
                }
            }
        }
    }, [task.input_type, task.cta]);

    const debouncedFetchOptions = useMemo(() => debounce(fetchOptions), [fetchOptions]);

    useEffect(() => {
        if (showOptions && (!options.length || !task.options || task.options.length === 0)) {
            fetchOptions();
        }
    }, [showOptions]);

    useEffect(() => {
        if (Array.isArray(task.options) && task.options.length > 0) {
            setAllOptions(task.options);
            setOptions(task.options);
        }
    }, [task.options]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'Enter' && task.responses?.[0]) {
                handleSave(index, task);
            }
        };

        if (task.input_type === 'TEXT') {
            document.addEventListener('keypress', handleKeyPress);
        }

        return () => document.removeEventListener('keypress', handleKeyPress);
    }, [task.responses, handleSave, index, task]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key !== 'Escape') return;
            if (showOptions) setShowOptions(false);
            else handleCloseInputBar(index, null);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showOptions, handleCloseInputBar, index]);

    if (!task) return null;

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && task.input_type === 'TEXT' && task.responses?.[0]) {
            handleSave(index, task);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchTerm(query);
        // If local options are available, filter locally; otherwise query the API
        if (Array.isArray(task.options) && task.options.length > 0) {
            if (!query) {
                setOptions(allOptions);
            } else {
                const lowered = query.toLowerCase();
                const filtered = allOptions.filter(opt => (opt.label || '').toLowerCase().includes(lowered));
                setOptions(filtered);
            }
        } else {
            debouncedFetchOptions(query);
        }
    };

    const getOptionLabel = (value) => {
        return options.find(opt => opt.value === value)?.label ||
            allOptions.find(opt => opt.value === value)?.label ||
            task.options?.find(opt => opt.value === value)?.label ||
            (task.responses || []).find(response => getResponseValue(response) === value)?.label ||
            value;
    };

    const renderSearchBar = () => (
        <div className="border-b border-blue-200 bg-white p-2">
            <div className="relative">
                <input
                    type="text"
                    className="w-full px-3 py-2 pr-8 text-sm border border-blue-300 rounded focus:outline-none focus:border-blue-500"
                    placeholder="Search options..."
                    value={searchTerm}
                    onChange={handleSearch}
                    autoFocus
                />
                <span className="material-icons absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    search
                </span>
            </div>
        </div>
    );

    const renderTextInput = () => (
        <div className="p-4">
            <div className="flex">
                <div className={`flex-1 relative`}>
                    <input
                        type={task.is_repeater ? "number" : "text"}
                        className={`w-full px-4 py-3 text-base border border-blue-300 rounded-l-md focus:outline-none focus:border-blue-500 transition-all ${isFocused ? 'ring-1 ring-blue-400' : ''}`}
                        placeholder={task.placeholder || "Type your response..."}
                        value={task.responses?.[0]?.label || ''}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        autoFocus
                    />
                    {task.responses?.[0] && (
                        <button
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            onClick={() => handleInputChange(index, '')}
                        >
                            <span className="material-icons text-base">cancel</span>
                        </button>
                    )}
                </div>
                <button
                    className={`bg-blue-600 text-white px-4 py-3 rounded-r-md transition-all flex items-center gap-2 text-base ${!task.responses?.[0] ? 'opacity-70 cursor-not-allowed bg-blue-400' : 'hover:bg-blue-700'}`}
                    onClick={() => task.responses?.[0] && handleSave(index, task)}
                    disabled={!task.responses?.[0]}
                >
                    <span className="material-icons text-base">send</span>
                    <span className="hidden sm:inline">Send</span>
                </button>
            </div>
        </div>
    );

    const renderInputComponent = () => {
        switch (task.input_type) {
            case 'SELECT': {
                const selectedValue = getResponseValue(task.responses?.[0]);
                const selectedLabel = task.responses?.[0] ? getOptionLabel(selectedValue) : '';
                return (
                    <div className="p-4">
                        <div
                            className={`flex items-center justify-between bg-white border ${showOptions ? 'border-blue-500' : 'border-blue-300'} rounded-md px-4 py-3 cursor-pointer hover:border-blue-500 transition-all`}
                            onClick={() => setShowOptions(!showOptions)}
                        >
                            <div className="flex items-center">
                                <span className="material-icons text-blue-600 mr-2 text-base">
                                    {task.responses?.[0] ? 'check_circle' : 'radio_button_unchecked'}
                                </span>
                                <span className={`text-base ${task.responses?.[0] ? 'text-gray-800' : 'text-gray-500'}`}>
                                    {selectedLabel || task.placeholder || "Select an option..."}
                                </span>
                            </div>
                            <span
                                className="material-icons text-gray-500 text-base"
                            >
                                {isLoading ? 'sync' : (showOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down')}
                            </span>
                        </div>

                        {showOptions && (
                            <div className="mt-2 bg-white border border-blue-300 rounded-md overflow-hidden">
                                {renderSearchBar()}

                                <div className="max-h-[16rem] overflow-y-auto">
                                    {isLoading ? (
                                        <div className="px-4 py-3 text-gray-500 text-center">
                                            <span className="material-icons animate-spin inline-block mr-2">sync</span>
                                            Loading options...
                                        </div>
                                    ) : options.length > 0 ? (
                                        options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${selectedValue === option.value ? 'bg-blue-100 text-blue-800' : 'text-gray-700'}`}
                                                onClick={() => {
                                                    handleMultiSelectChange(index, [option]);
                                                    setShowOptions(false);
                                                    setTimeout(() => handleSave(index, { ...task, responses: [option] }), 100);
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <span className={`w-5 h-5 mr-2 flex items-center justify-center ${selectedValue === option.value ? 'text-blue-600' : 'text-gray-400'}`}>
                                                        <span className="material-icons text-base">
                                                            {selectedValue === option.value ? 'check_circle' : 'radio_button_unchecked'}
                                                        </span>
                                                    </span>
                                                    <span className="text-base">{option.label}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-3 text-gray-500 text-center">
                                            No options available
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            case 'MULTI_SELECT': {
                const selectedValues = (task.responses || []).map(getResponseValue);
                return (
                    <div className="p-4">
                        <div
                            className={`flex items-center justify-between bg-white border ${showOptions ? 'border-blue-500' : 'border-blue-300'} rounded-md px-4 py-3 cursor-pointer hover:border-blue-500 transition-all`}
                            onClick={() => setShowOptions(!showOptions)}
                        >
                            <div className="flex items-center">
                                <span className="material-icons text-blue-600 mr-2 text-base">
                                    {selectedValues.length ? 'fact_check' : 'checklist'}
                                </span>
                                <span className={`text-base truncate ${selectedValues.length ? 'text-gray-800' : 'text-gray-500'}`}>
                                    {selectedValues.length
                                        ? `${selectedValues.length} option${selectedValues.length > 1 ? 's' : ''} selected`
                                        : task.placeholder || "Select options..."}
                                </span>
                            </div>
                            <span
                                className="material-icons text-gray-500 text-base"
                            >
                                {isLoading ? 'sync' : (showOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down')}
                            </span>
                        </div>

                        {showOptions && (
                            <div className="mt-2 bg-white border border-blue-300 rounded-md overflow-hidden">
                                {renderSearchBar()}

                                <div className="max-h-[16rem] overflow-y-auto">
                                    {isLoading ? (
                                        <div className="px-4 py-3 text-gray-500 text-center">
                                            <span className="material-icons animate-spin inline-block mr-2">sync</span>
                                            Loading options...
                                        </div>
                                    ) : options.length > 0 ? (
                                        options.map((option, optIdx) => {
                                            const isSelected = selectedValues.includes(option.value);
                                            return (
                                                <div
                                                    key={optIdx}
                                                    className="px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors text-gray-700"
                                                    onClick={() => {
                                                        const nextValues = isSelected
                                                            ? selectedValues.filter(value => value !== option.value)
                                                            : [...selectedValues, option.value];

                                                        handleMultiSelectChange(index, nextValues.map(value => ({
                                                            value,
                                                            label: getOptionLabel(value)
                                                        })));
                                                    }}
                                                >
                                                    <div className="flex items-center">
                                                        <span
                                                            className={`w-5 h-5 mr-2 flex items-center justify-center ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}
                                                        >
                                                            <span className="material-icons text-base">
                                                                {isSelected ? 'check_box' : 'check_box_outline_blank'}
                                                            </span>
                                                        </span>
                                                        <span className="text-base">{option.label}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="px-4 py-3 text-gray-500 text-center">
                                            No options available
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            className={`mt-3 w-full bg-blue-600 text-white px-4 py-3 rounded-md transition-all flex items-center justify-center gap-2 text-base ${!selectedValues.length ? 'opacity-70 cursor-not-allowed bg-blue-400' : 'hover:bg-blue-700'}`}
                            onClick={() => {
                                if (!selectedValues.length) return;
                                setShowOptions(false);
                                handleSave(index, task);
                            }}
                            disabled={!selectedValues.length}
                        >
                            <span className="material-icons text-base">check</span>
                            Save
                        </button>
                    </div>
                );
            }
            case 'TEXT':
            default:
                return renderTextInput();
        }
    };

    // The sub-header that owns this task is animated (transformed) and clips its
    // overflow, so the sheet is portalled out of it and into the chat panel, where
    // it docks to the bottom of the chat window. Without a chat panel around it
    // (support track view), it falls back to the bottom of the viewport.
    const sheet = (
        <div
            className={`${isDockedToChat ? 'absolute' : 'fixed'} inset-0 z-[60] flex flex-col justify-end`}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="absolute inset-0 bg-black bg-opacity-40"
                onClick={() => handleCloseInputBar(index, null)}
            ></div>
            <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'tween', duration: 0.25 }}
                className={`relative w-full bg-white border-t border-blue-300 shadow-lg rounded-t-lg max-h-[85%] flex flex-col ${isDockedToChat ? '' : 'max-w-3xl mx-auto'}`}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-blue-200 flex-shrink-0">
                    <h3
                        className="text-base font-medium text-blue-900 flex items-center"
                    >
                        <span className="material-icons text-blue-700 mr-2 text-base">assignment</span>
                        {task.title || "Enter your response"}
                    </h3>
                    <button
                        className="text-blue-600 hover:text-blue-800 transition-colors p-2 rounded-full hover:bg-blue-100"
                        onClick={() => handleCloseInputBar(index, null)}
                    >
                        <span className="material-icons text-base">close</span>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {renderInputComponent()}
                </div>
            </motion.div>
        </div>
    );

    return (
        <>
            <span ref={anchorRef} className="hidden"></span>
            {portalTarget && createPortal(sheet, portalTarget)}
        </>
    );
}
